import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm, Switch, Tag, Checkbox } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../services/api';

interface Branch {
  id: number;
  branchCode: string;
  branchName: string;
  branchType: string;
  address?: string;
  phone?: string;
  email?: string;
  managerName?: string;
  taxNumber?: string;
  isMainBranch: boolean;
  parentBranchId?: number;
  parentBranchName?: string;
  isActive: boolean;
  createdAt: string;
}

const Branches: React.FC = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [branchTypes, setBranchTypes] = useState<Array<{ value: number; label: string; name: string }>>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    loadBranches();
    loadBranchTypes();
  }, []);

  const loadBranches = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/branches');
      setBranches(data);
    } catch (error) {
      message.error('Şubeler yüklenirken hata oluştu');
      console.error('Branches load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBranchTypes = async () => {
    try {
      const { data } = await api.get('/branches/branch-types');
      setBranchTypes(data);
    } catch (error) {
      console.error('Branch types load error:', error);
    }
  };

  const handleAdd = () => {
    setEditingBranch(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (branch: Branch) => {
    setEditingBranch(branch);
    // BranchType string'i ID'ye çevir
    const branchTypeValue = branchTypes.find(type => type.name === branch.branchType)?.value || branchTypes[0]?.value;
    form.setFieldsValue({
      ...branch,
      branchType: branchTypeValue
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await api.delete(`/branches/${id}`);
      message.success('Şube silindi');
      loadBranches();
    } catch (error) {
      message.error('Şube silinirken hata oluştu');
      console.error('Delete error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    try {
      await api.patch(`/branches/${id}/toggle-active`);
      message.success(`Şube ${!currentStatus ? 'aktif' : 'pasif'} edildi`);
      loadBranches();
    } catch (error) {
      message.error('Durum değiştirilirken hata oluştu');
      console.error('Toggle active error:', error);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      if (editingBranch) {
        await api.put(`/branches/${editingBranch.id}`, values);
        message.success('Şube güncellendi');
      } else {
        await api.post('/branches', values);
        message.success('Şube eklendi');
      }
      setModalVisible(false);
      form.resetFields();
      loadBranches();
    } catch (error) {
      message.error('Şube kaydedilirken hata oluştu');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBranchTypeColor = (type: string) => {
    switch (type) {
      case 'Ofis': return 'blue';
      case 'Mağaza': return 'green';
      case 'Depo': return 'orange';
      default: return 'default';
    }
  };

  const getBranchTypeText = (type: string) => {
    // Artık TypeName direkt geliyor, olduğu gibi döndür
    return type;
  };

  const columns: ColumnsType<Branch> = [
    {
      title: 'Şube Kodu',
      dataIndex: 'branchCode',
      key: 'branchCode',
      width: 120,
    },
    {
      title: 'Şube Adı',
      dataIndex: 'branchName',
      key: 'branchName',
      render: (name: string, record: Branch) => (
        <div>
          <strong>{name}</strong>
          {record.isMainBranch && <Tag color="red" style={{ marginLeft: 8 }}>Ana Şube</Tag>}
        </div>
      ),
    },
    {
      title: 'Tip',
      dataIndex: 'branchType',
      key: 'branchType',
      width: 120,
      render: (type: string) => (
        <Tag color={getBranchTypeColor(type)}>
          {getBranchTypeText(type)}
        </Tag>
      ),
    },
    {
      title: 'Müdür',
      dataIndex: 'managerName',
      key: 'managerName',
      render: (text: string) => text || '-'
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      render: (text: string) => text || '-'
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
      render: (text: string) => text || '-'
    },
    {
      title: 'Üst Şube',
      dataIndex: 'parentBranchName',
      key: 'parentBranchName',
      render: (text: string) => text || '-'
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean, record: Branch) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleActive(record.id, isActive)}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
        />
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Bu şubeyi silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Şubeler"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Yeni Şube
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={branches}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
        }}
      />

      <Modal
        title={editingBranch ? 'Şube Düzenle' : 'Yeni Şube'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="branchCode"
            label="Şube Kodu"
            rules={[{ required: true, message: 'Şube kodu giriniz' }]}
          >
            <Input placeholder="Örn: MAIN, BR001" />
          </Form.Item>

          <Form.Item
            name="branchName"
            label="Şube Adı"
            rules={[{ required: true, message: 'Şube adı giriniz' }]}
          >
            <Input placeholder="Şube adı" />
          </Form.Item>

          <Form.Item
            name="branchType"
            label="Şube Tipi"
            rules={[{ required: true, message: 'Şube tipi seçiniz' }]}
          >
            <Select placeholder="Şube tipi seçiniz">
              {branchTypes.map(type => (
                <Select.Option key={type.value} value={type.value}>
                  {type.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="parentBranchId"
            label="Üst Şube (Opsiyonel)"
          >
            <Select placeholder="Üst şube seçin" allowClear>
              {branches
                .filter(b => !editingBranch || b.id !== editingBranch.id)
                .map(b => (
                  <Select.Option key={b.id} value={b.id}>
                    {b.branchName}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="managerName"
            label="Müdür Adı"
          >
            <Input placeholder="Müdür adı" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Telefon"
          >
            <Input placeholder="Telefon numarası" />
          </Form.Item>

          <Form.Item
            name="email"
            label="E-posta"
            rules={[{ type: 'email', message: 'Geçerli bir e-posta adresi giriniz' }]}
          >
            <Input placeholder="E-posta adresi" />
          </Form.Item>

          <Form.Item
            name="address"
            label="Adres"
          >
            <Input.TextArea placeholder="Adres bilgisi" rows={3} />
          </Form.Item>

          <Form.Item
            name="taxNumber"
            label="Vergi Numarası"
          >
            <Input placeholder="Vergi numarası" />
          </Form.Item>

          <Form.Item
            name="isMainBranch"
            valuePropName="checked"
          >
            <Checkbox>Ana Şube</Checkbox>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingBranch ? 'Güncelle' : 'Kaydet'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Branches;
